import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { usePolling } from "@/hooks/usePolling";
import { SEO } from "@/components/SEO";
import TestimonialDocument from "@/components/TestimonialDocument";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  Certificate, PaperPlane, MagnifyingGlass, Download, Eye,
  FilePdf, Image, X, Check, Briefcase, Buildings, User, UserCircle
} from "@phosphor-icons/react";

export default function TestimonialsPage() {
  const { token, user } = useAuth();
  const [tab, setTab] = useState("received");
  const [received, setReceived] = useState([]);
  const [given, setGiven] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [previewTestimonial, setPreviewTestimonial] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const docRef = useRef(null);

  // Create form
  const [memberSearch, setMemberSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [serviceTaken, setServiceTaken] = useState("");
  const [testimonialText, setTestimonialText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const searchTimeout = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [recRes, givRes] = await Promise.all([
        axios.get(`${API}/testimonials/received`, { headers }),
        axios.get(`${API}/testimonials/given`, { headers })
      ]);
      setReceived(recRes.data);
      setGiven(givRes.data);
    } catch (e) { console.error("Fetch testimonials failed:", e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);
  usePolling(fetchData, 15000);

  // Member search
  useEffect(() => {
    if (memberSearch.length < 2) { setSearchResults([]); return; }
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/member-lookup?q=${encodeURIComponent(memberSearch)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = res.data;
        if (data.found) {
          const members = data.members || (data.member ? [data.member] : []);
          setSearchResults(members.filter(m => m.id !== user?.id));
        } else {
          setSearchResults([]);
        }
      } catch { setSearchResults([]); }
    }, 300);
  }, [memberSearch, token, user?.id]);

  const handleCreate = async () => {
    if (!selectedMember || !serviceTaken.trim() || !testimonialText.trim()) {
      toast.error("Please fill all fields"); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/testimonials`, {
        to_user_id: selectedMember.id,
        service_taken: serviceTaken.trim(),
        testimonial_text: testimonialText.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Testimonial sent successfully!");
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send testimonial");
    } finally { setSubmitting(false); }
  };

  const resetForm = () => {
    setSelectedMember(null);
    setMemberSearch("");
    setSearchResults([]);
    setServiceTaken("");
    setTestimonialText("");
  };

  const handleToggleProfile = async (testimonialId) => {
    try {
      const res = await axios.patch(`${API}/testimonials/${testimonialId}/toggle-profile`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newVal = res.data.show_on_profile;
      setReceived(prev => prev.map(t => t.id === testimonialId ? { ...t, show_on_profile: newVal } : t));
      toast.success(newVal ? "Added to your public profile" : "Removed from public profile");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const downloadAs = async (format) => {
    if (!docRef.current) return;
    setDownloading(true);
    try {
      // Clone the document off-screen to avoid visible resize
      const clone = docRef.current.cloneNode(true);
      clone.style.position = "fixed";
      clone.style.left = "-9999px";
      clone.style.top = "0";
      clone.style.transform = "none";
      clone.style.width = "794px";
      clone.style.height = "1123px";
      document.body.appendChild(clone);

      const canvas = await html2canvas(clone, {
        scale: 3, useCORS: true, allowTaint: true, backgroundColor: "#ffffff",
        width: 794, height: 1123,
        logging: false,
        imageTimeout: 15000
      });

      document.body.removeChild(clone);

      if (format === "png") {
        const link = document.createElement("a");
        link.download = `BCH_Testimonial_${previewTestimonial.to_user_name.replace(/\s+/g, "_")}.png`;
        link.href = canvas.toDataURL("image/png");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        const imgData = canvas.toDataURL("image/jpeg", 0.95);
        const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
        pdf.addImage(imgData, "JPEG", 0, 0, 210, 297);
        pdf.save(`BCH_Testimonial_${previewTestimonial.to_user_name.replace(/\s+/g, "_")}.pdf`);
      }
      toast.success(`Downloaded as ${format.toUpperCase()}`);
    } catch (e) {
      console.error("Download failed:", e);
      toast.error("Download failed — please try again");
    } finally { setDownloading(false); }
  };

  const list = tab === "received" ? received : given;

  if (loading) return <><div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" /></div></>;

  return (
    <>
    <div className="space-y-6" data-testid="testimonials-page">
      <SEO title="Testimonials - BCH" />

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-primary text-2xl font-bold flex items-center gap-2">
            <Certificate className="w-7 h-7 text-bch-gold" weight="duotone" /> Testimonials
          </h1>
          <p className="text-muted text-sm mt-1">Appreciate fellow members for their great work</p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreate(true); }}
          className="bg-bch-teal hover:bg-bch-teal-dark text-white" data-testid="write-testimonial-btn">
          <PaperPlane className="w-4 h-4 mr-2" weight="duotone" /> Write Testimonial
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {["received", "given"].map(t => (
          <Button key={t} variant={tab === t ? "default" : "outline"} size="sm"
            onClick={() => setTab(t)}
            className={tab === t ? "bg-bch-teal text-white" : "border-[var(--border-color)] text-muted"}
            data-testid={`tab-${t}`}>
            {t === "received" ? "Received" : "Given"}
            <span className="ml-1.5 text-xs opacity-70">({t === "received" ? received.length : given.length})</span>
          </Button>
        ))}
      </div>

      {/* Testimonial Cards */}
      {list.length === 0 ? (
        <Card className="bg-card border-[var(--border-color)]">
          <CardContent className="p-12 text-center">
            <Certificate className="w-14 h-14 text-muted mx-auto mb-3" weight="duotone" />
            <p className="text-muted text-sm">
              {tab === "received" ? "No testimonials received yet" : "You haven't written any testimonials yet"}
            </p>
            {tab === "given" && (
              <Button onClick={() => { resetForm(); setShowCreate(true); }} variant="outline" size="sm" className="mt-3 border-bch-teal text-bch-teal">
                Write your first testimonial
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {list.map(t => (
            <Card key={t.id} className="bg-card border-[var(--border-color)] hover:border-bch-gold/30 transition-colors" data-testid={`testimonial-card-${t.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-bch-teal to-emerald-600 flex items-center justify-center text-white font-bold text-xs shrink-0">
                        {(tab === "received" ? t.from_user_name : t.to_user_name)?.charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <p className="text-primary font-semibold text-sm truncate">
                          {tab === "received" ? t.from_user_name : t.to_user_name}
                        </p>
                        <p className="text-muted text-xs truncate">
                          {tab === "received" ? t.from_business_name : t.to_business_name} &bull; {tab === "received" ? t.from_chapter_name : t.to_chapter_name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="px-2 py-0.5 rounded-full bg-bch-gold/10 text-bch-gold text-xs font-semibold">
                        <Briefcase className="w-3 h-3 inline mr-1" weight="duotone" />
                        {t.service_taken}
                      </span>
                      <span className="text-muted text-xs">{new Date(t.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-primary text-sm leading-relaxed line-clamp-2 italic">&ldquo;{t.testimonial_text}&rdquo;</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {tab === "received" && !t.show_on_profile && (
                      <Button onClick={() => handleToggleProfile(t.id)}
                        variant="outline" size="sm"
                        className="border-[var(--border-color)] text-muted hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-300"
                        data-testid={`toggle-profile-${t.id}`}>
                        <UserCircle className="w-4 h-4 mr-1" weight="duotone" />
                        Add to Profile
                      </Button>
                    )}
                    <Button onClick={() => setPreviewTestimonial(t)} variant="outline" size="sm"
                      className="border-bch-gold/30 text-bch-gold hover:bg-bch-gold/5 shrink-0" data-testid={`view-${t.id}`}>
                      <Eye className="w-4 h-4 mr-1" /> View
                    </Button>
                  </div>
                </div>
                {tab === "received" && t.show_on_profile && (
                  <div className="mt-3 pt-3 border-t border-[var(--border-color)] flex items-center justify-between">
                    <span className="text-emerald-600 text-xs font-medium flex items-center gap-1.5">
                      <UserCircle className="w-4 h-4" weight="fill" />
                      Visible on your public profile
                    </span>
                    <Button onClick={() => handleToggleProfile(t.id)}
                      variant="outline" size="sm"
                      className="border-red-200 text-red-500 hover:bg-red-50 hover:border-red-300 h-7 text-xs"
                      data-testid={`remove-profile-${t.id}`}>
                      Remove from Profile
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Write Testimonial Dialog */}
      <Dialog open={showCreate} onOpenChange={v => { if (!v) resetForm(); setShowCreate(v); }}>
        <DialogContent className="bg-card border-[var(--border-color)] max-w-lg">
          <DialogHeader><DialogTitle className="text-primary flex items-center gap-2"><PaperPlane className="w-5 h-5 text-bch-teal" weight="duotone" /> Write a Testimonial</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Member Search */}
            {!selectedMember ? (
              <div>
                <label className="text-muted text-xs uppercase tracking-wider mb-1.5 block">Select Member</label>
                <div className="relative">
                  <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                  <Input placeholder="Search by name or business..." value={memberSearch}
                    onChange={e => setMemberSearch(e.target.value)}
                    className="pl-9 bg-surface border-[var(--border-color)] text-primary" data-testid="member-search" />
                </div>
                {searchResults.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto space-y-1 rounded-lg border border-[var(--border-color)] bg-surface p-1" data-testid="search-results">
                    {searchResults.slice(0, 8).map(m => (
                      <button key={m.id} onClick={() => { setSelectedMember(m); setSearchResults([]); setMemberSearch(""); }}
                        className="w-full text-left p-2.5 rounded-lg hover:bg-bch-teal/5 transition-colors flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-bch-teal/10 flex items-center justify-center text-bch-teal text-xs font-bold shrink-0">
                          {m.full_name?.charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-primary text-sm font-medium truncate">{m.full_name}</p>
                          <p className="text-muted text-xs truncate">{m.business_name} &bull; {m.chapter_name}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-xl" data-testid="selected-member">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-emerald-500/10 flex items-center justify-center">
                    <Check className="w-4 h-4 text-emerald-500" weight="bold" />
                  </div>
                  <div>
                    <p className="text-primary text-sm font-semibold">{selectedMember.full_name}</p>
                    <p className="text-muted text-xs">{selectedMember.business_name} &bull; {selectedMember.chapter_name}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedMember(null)} className="text-muted">
                  <X className="w-4 h-4" /> Change
                </Button>
              </div>
            )}

            {/* Service */}
            <div>
              <label className="text-muted text-xs uppercase tracking-wider mb-1.5 block">Service / Product Taken</label>
              <Input placeholder="e.g., IT Consulting, Interior Design, Legal Advice..." value={serviceTaken}
                onChange={e => setServiceTaken(e.target.value)}
                className="bg-surface border-[var(--border-color)] text-primary" data-testid="service-input" />
            </div>

            {/* Testimonial */}
            <div>
              <label className="text-muted text-xs uppercase tracking-wider mb-1.5 block">Your Testimonial</label>
              <Textarea placeholder="Share your experience... What made their service exceptional? How did they help your business?"
                value={testimonialText} onChange={e => { if (e.target.value.length <= 1000) setTestimonialText(e.target.value); }}
                rows={5} className="bg-surface border-[var(--border-color)] text-primary resize-none" data-testid="testimonial-input" />
              <p className="text-muted text-xs mt-1 text-right">{testimonialText.length}/1000</p>
            </div>

            <Button onClick={handleCreate} disabled={submitting || !selectedMember || !serviceTaken.trim() || !testimonialText.trim()}
              className="w-full bg-bch-teal hover:bg-bch-teal-dark text-white" data-testid="submit-testimonial-btn">
              {submitting ? "Sending..." : "Send Testimonial"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview & Download Dialog */}
      <Dialog open={!!previewTestimonial} onOpenChange={v => { if (!v) setPreviewTestimonial(null); }}>
        <DialogContent className="bg-card border-[var(--border-color)] max-w-[860px] max-h-[90vh] overflow-y-auto p-4 [&>button]:top-3 [&>button]:right-3">
          <DialogHeader>
            <DialogTitle className="text-primary flex items-center justify-between pr-8">
              <span className="flex items-center gap-2"><Certificate className="w-5 h-5 text-bch-gold" weight="duotone" /> Testimonial Preview</span>
              <div className="flex gap-2">
                <Button onClick={() => downloadAs("png")} disabled={downloading} size="sm"
                  className="bg-bch-teal hover:bg-bch-teal-dark text-white" data-testid="download-png-btn">
                  <Image className="w-4 h-4 mr-1" weight="duotone" /> PNG
                </Button>
                <Button onClick={() => downloadAs("pdf")} disabled={downloading} size="sm"
                  className="bg-bch-gold hover:bg-bch-gold/90 text-black" data-testid="download-pdf-btn">
                  <FilePdf className="w-4 h-4 mr-1" weight="duotone" /> PDF
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          {previewTestimonial && (
            <div className="flex justify-center mt-2" style={{ overflow: "auto" }}>
              <div style={{ transform: "scale(0.72)", transformOrigin: "top center" }}>
                <TestimonialDocument ref={docRef} testimonial={previewTestimonial} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
}
